import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import type { Chapter } from '../types';

interface ReorderChaptersDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  chapters: Chapter[];
  onReorderChapters: (chapterIds: number[]) => void;
  isSaving: boolean;
}

interface ChapterItemProps {
  chapter: Chapter;
  isDragging?: boolean;
}

const ChapterItem = React.memo(React.forwardRef<HTMLDivElement, ChapterItemProps & React.HTMLAttributes<HTMLDivElement>>(
  ({ chapter, isDragging, style, ...props }, ref) => {
    return (
      <Card
        ref={ref}
        style={style}
        className={`relative rounded-xl border-border/40 shadow-sm bg-card transition-colors ${isDragging ? 'opacity-30' : 'hover:bg-muted/30'}`}
        {...props}
      >
        <CardContent className="p-3 flex items-center">
          <div className="mr-2.5 cursor-move hover:text-primary transition-colors">
            <GripVertical className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-medium text-sm truncate select-none">{chapter.name || chapter.title}</div>
            {chapter.description && (
              <div className="text-xs text-muted-foreground mt-1 line-clamp-2 select-none">{chapter.description}</div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }
));

ChapterItem.displayName = 'ChapterItem';

const SortableChapterItem: React.FC<{ chapter: Chapter }> = ({ chapter }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: chapter.id.toString() });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <ChapterItem
      ref={setNodeRef}
      style={style}
      chapter={chapter}
      isDragging={isDragging}
      {...attributes}
      {...listeners}
    />
  );
};

export const ReorderChaptersDialog: React.FC<ReorderChaptersDialogProps> = ({
  open,
  onOpenChange,
  chapters,
  onReorderChapters,
  isSaving
}) => {
  const [orderedChapters, setOrderedChapters] = useState<Chapter[]>([]);

  // Update local ordered chapters whenever the chapters prop changes or dialog opens
  useEffect(() => {
    if (open) {
      setOrderedChapters([...chapters]);
    }
  }, [chapters, open]);

  // Configure sensors for drag events
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5, // Require 5px movement before drag starts to prevent accidental clicks
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over) {
      return;
    }

    if (active.id !== over.id) {
      setOrderedChapters((items) => {
        const oldIndex = items.findIndex(item => item.id.toString() === active.id);
        const newIndex = items.findIndex(item => item.id.toString() === over.id);

        // Get ordered list after move
        const newItems = arrayMove(items, oldIndex, newIndex);

        // Update the _order property explicitly for each chapter
        return newItems.map((chapter, index) => ({
          ...chapter,
          _order: index // Explicitly set the _order field based on new position
        }));
      });
    }
  };

  const handleSaveOrder = () => {
    console.log('Saving chapter order:', orderedChapters.map(ch => ({ id: ch.id, _order: ch._order })));

    // Pass the ordered chapter IDs to the parent component
    onReorderChapters(orderedChapters.map(chapter => chapter.id));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] sm:rounded-2xl border-border/60 shadow-2xl p-0 overflow-hidden gap-0">
        <DialogHeader className="p-6 pb-4 border-b border-border/40 bg-muted/5">
          <DialogTitle className="text-xl font-semibold text-foreground tracking-tight">Reorder Chapters</DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            Drag and drop chapters to reorder them. The order will be reflected in the timeline.
          </DialogDescription>
        </DialogHeader>

        <div className="p-6 max-h-[60vh] overflow-y-auto bg-background/50">
          {chapters.length === 0 ? (
            <div className="text-center p-4 text-muted-foreground text-sm">
              No chapters yet. Add chapters to the timeline first.
            </div>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={orderedChapters.map(chapter => chapter.id.toString())}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-3">
                  {orderedChapters.map((chapter) => (
                    <SortableChapterItem key={chapter.id} chapter={chapter} />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </div>

        <DialogFooter className="p-6 bg-muted/5 border-t border-border/40 gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="rounded-xl hover:bg-muted/50">
            Cancel
          </Button>
          <Button
            onClick={handleSaveOrder}
            disabled={isSaving || chapters.length === 0}
            className="rounded-xl shadow-sm font-medium"
          >
            {isSaving ? (
              <>
                <span className="animate-spin mr-2">⏳</span>
                Saving...
              </>
            ) : (
              'Save Order'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};